import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  useQuery,
  getLocationBySlug,
  getUserMembership,
  getMyAccessRequest,
  joinLocationWithCode,
  joinLocationAsMember,
  requestLocationAccess,
} from "wasp/client/operations";
import {
  KeyRound,
  ClipboardList,
  Building2,
  ArrowRight,
  LayoutGrid,
  UserPlus,
} from "lucide-react";

export default function LocationLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: user } = useAuth();

  const { data: location, isLoading } = useQuery(
    getLocationBySlug,
    { slug: slug || "" },
    { enabled: !!slug },
  );
  const loc = location as any;

  const { data: membership, refetch: refetchMembership } = useQuery(
    getUserMembership,
    { locationId: loc?.id || "" },
    { enabled: !!user && !!loc?.id },
  );
  const { data: accessRequest, refetch: refetchRequest } = useQuery(
    getMyAccessRequest,
    { locationId: loc?.id || "" },
    { enabled: !!user && !!loc?.id },
  );

  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [unitNumber, setUnitNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const mem = membership as any;
  const req = accessRequest as any;

  const statusLabel = useMemo(() => {
    if (["ADMIN", "MANAGER"].includes(mem?.role)) {
      return "You manage this location.";
    }
    if (mem?.isVerified) {
      return "You're a member of this location.";
    }
    if (req?.status === "PENDING") {
      return "Your membership request is waiting for approval. Until then you're a visitor and can see public posts.";
    }
    if (req?.status === "REJECTED") return "Your previous membership request was declined.";
    if (user) return "You're visiting. You can see public posts; become a member to post and see members-only content.";
    return null;
  }, [mem, req, user]);

  async function onBecomeMember() {
    if (!loc?.id) return;
    if (!user) {
      navigate(`/login?redirect=/locations/${slug}`);
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await joinLocationAsMember({
        locationId: loc.id,
        unitNumber: unitNumber || undefined,
      });
      setInfo("Welcome! You're now a member.");
      await Promise.all([refetchMembership(), refetchRequest()]);
    } catch (err: any) {
      setError(err?.message || "Could not join");
    } finally {
      setBusy(false);
    }
  }

  async function onJoinCode(e: FormEvent) {
    e.preventDefault();
    if (!loc?.id) return;
    if (!user) {
      navigate(`/login?redirect=/locations/${slug}`);
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await joinLocationWithCode({ locationId: loc.id, code });
      setInfo("You're in — community and business access granted.");
      setCode("");
      await Promise.all([refetchMembership(), refetchRequest()]);
    } catch (err: any) {
      setError(err?.message || "Could not join with that code");
    } finally {
      setBusy(false);
    }
  }

  async function onRequestAccess(e: FormEvent) {
    e.preventDefault();
    if (!loc?.id) return;
    if (!user) {
      navigate(`/login?redirect=/locations/${slug}`);
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await requestLocationAccess({
        locationId: loc.id,
        message,
        unitNumber: unitNumber || undefined,
      });
      setInfo("Request submitted. A manager of this location will review it.");
      setMessage("");
      await Promise.all([refetchMembership(), refetchRequest()]);
    } catch (err: any) {
      setError(err?.message || "Could not submit request");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 px-4 text-slate-500">
        Loading building…
      </div>
    );
  }

  if (!loc) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-slate-50 px-4">
        <h1 className="text-2xl font-bold">Building not found</h1>
        <Link to="/" className="min-h-11 text-sky-700 underline">
          Go home
        </Link>
      </div>
    );
  }

  const allowCode = loc.allowDirectCodeJoin !== false;
  const needsApproval = loc.requireMemberApproval !== false;
  const allowRequest = needsApproval && loc.allowAccessRequest !== false;
  const showJoinForms = !mem?.isVerified;

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-100 via-white to-sky-50 pb-[env(safe-area-inset-bottom)]">
      <header className="sticky top-0 z-10 border-b border-slate-200/80 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <div className="flex min-w-0 items-center gap-2 text-slate-800">
            <Building2 className="h-5 w-5 shrink-0 text-sky-700" />
            <span className="truncate text-sm font-semibold tracking-tight">
              Building access
            </span>
          </div>
          {user ? (
            <Link
              to="/dashboard"
              className="inline-flex min-h-10 shrink-0 items-center text-sm font-medium text-sky-700"
            >
              Dashboard
            </Link>
          ) : (
            <Link
              to={`/login?redirect=/locations/${slug}`}
              className="inline-flex min-h-10 shrink-0 items-center text-sm font-medium text-sky-700"
            >
              Log in
            </Link>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6 sm:py-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">
          {loc.locationCode || slug}
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          {loc.name}
        </h1>
        {loc.publicDescription && (
          <p className="mt-3 text-base leading-relaxed text-slate-600">
            {loc.publicDescription}
          </p>
        )}
        {(loc.city || loc.address) && (
          <p className="mt-2 text-sm text-slate-500">
            {[loc.address, loc.city, loc.state, loc.country]
              .filter(Boolean)
              .join(", ")}
          </p>
        )}

        {statusLabel && (
          <div className="mt-6 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-4 text-sm text-sky-900">
            <p>{statusLabel}</p>
            {mem?.isVerified && (
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Link
                  to="/dashboard"
                  className="inline-flex min-h-11 items-center justify-center gap-1 rounded-2xl bg-sky-800 px-4 text-sm font-semibold text-white"
                >
                  Open portal <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/my-buildings"
                  className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-2xl border border-sky-300 bg-white px-4 text-sm font-semibold text-sky-900"
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  My buildings
                </Link>
              </div>
            )}
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700"
          >
            {error}
          </p>
        )}
        {info && (
          <p
            role="status"
            className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800"
          >
            {info}
          </p>
        )}

        <div className="mt-8 space-y-4">
          {!needsApproval && showJoinForms && (
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-slate-700" />
                <h2 className="text-lg font-semibold">Become a member</h2>
              </div>
              <p className="mb-4 text-sm leading-relaxed text-slate-600">
                Members can post and see members-only content. No approval
                needed here.
              </p>
              <label className="mb-3 block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Unit / suite (optional)
                </span>
                <input
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value)}
                  placeholder="e.g. 12B"
                  autoComplete="off"
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3.5 text-base"
                />
              </label>
              <button
                type="button"
                onClick={onBecomeMember}
                disabled={busy}
                className="w-full rounded-2xl bg-sky-700 px-4 py-3.5 text-base font-semibold text-white disabled:opacity-50"
              >
                {user
                  ? busy
                    ? "Joining…"
                    : "Become a member"
                  : "Log in to join"}
              </button>
            </section>
          )}

          {allowCode && showJoinForms && (
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-slate-700" />
                <h2 className="text-lg font-semibold">Have an invite code?</h2>
              </div>
              <p className="mb-4 text-sm leading-relaxed text-slate-600">
                Enter the code from the location's management to become a
                member right away.
              </p>
              <form onSubmit={onJoinCode} className="space-y-3">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Access code
                  </span>
                  <input
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="e.g. 260A3C00"
                    inputMode="text"
                    autoComplete="one-time-code"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="go"
                    className="w-full rounded-2xl border border-slate-300 px-4 py-3.5 text-base tracking-[0.2em] uppercase"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-2xl bg-slate-900 px-4 py-3.5 text-base font-semibold text-white disabled:opacity-50"
                >
                  {user
                    ? busy
                      ? "Joining…"
                      : "Join building"
                    : "Log in to join"}
                </button>
              </form>
            </section>
          )}

          {allowRequest &&
            showJoinForms &&
            req?.status !== "PENDING" && (
              <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-slate-700" />
                  <h2 className="text-lg font-semibold">Request membership</h2>
                </div>
                <p className="mb-4 text-sm leading-relaxed text-slate-600">
                  Membership here needs approval. A manager of this location
                  will review your request.
                </p>
                <form onSubmit={onRequestAccess} className="space-y-3">
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Unit / suite (optional)
                    </span>
                    <input
                      value={unitNumber}
                      onChange={(e) => setUnitNumber(e.target.value)}
                      placeholder="e.g. 12B"
                      autoComplete="off"
                      enterKeyHint="next"
                      className="w-full rounded-2xl border border-slate-300 px-4 py-3.5 text-base"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Message (optional)
                    </span>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Why you need access"
                      rows={3}
                      enterKeyHint="send"
                      className="w-full rounded-2xl border border-slate-300 px-4 py-3.5 text-base"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full rounded-2xl bg-sky-700 px-4 py-3.5 text-base font-semibold text-white disabled:opacity-50"
                  >
                    {user
                      ? busy
                        ? "Submitting…"
                        : "Request membership"
                      : "Log in to request"}
                  </button>
                </form>
              </section>
            )}

          {needsApproval && !allowCode && !allowRequest && showJoinForms && (
            <p className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Membership requests are currently closed for this location.
              Contact its management.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
